/*
  # Updated News & Updates to use database
  
  1. Changes
    - Updated to fetch news posts from database instead of static data
    - Maintained existing UI and functionality
    - Added loading states and error handling
*/

import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';
import { NewsPost } from '../types/news';

const NewsUpdates = () => {
  const [visibleNews, setVisibleNews] = useState<Set<number>>(new Set());
  const [news, setNews] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const { data, error } = await supabase
          .from('news_posts')
          .select('*')
          .eq('is_published', true)
          .order('display_order', { ascending: true })
          .order('published_date', { ascending: false })
          .limit(6); // Show max 6 posts

        if (error) throw error;
        setNews(data || []);
      } catch (err) {
        console.error('Error fetching news:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  useEffect(() => {
    if (news.length === 0) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-news-index'));
            if (!isNaN(index)) {
              setVisibleNews((prev) => new Set([...prev, index]));
            }
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '50px'
      }
    );

    document.querySelectorAll('[data-news-index]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [news]);

  return (
    <section className="bg-[#F2F3F4] py-16">
      <div className="px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-12">
          <h2 className="font-barrio text-4xl md:text-5xl text-[#2A382A]">
            News & Updates
          </h2>
          {/* <Link
            to="/blog"
            className="hidden lg:flex items-center justify-between px-3 py-2 bg-[#E85C0D] text-white rounded-[1rem] hover:bg-[#ff6a0d] transition-all duration-300 hover:scale-105 w-auto text-sm"
          >
            See All News
            <span className="bg-white/20 p-2 rounded-lg ml-6">
              <ArrowRight className="w-5 h-5" />
            </span>
          </Link> */}
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-8">
          {loading ? (
            <div className="col-span-full text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2A382A]"></div>
              <p className="mt-4 text-[#2A382A]/70">Loading news...</p>
            </div>
          ) : news.length === 0 ? (
            <div className="col-span-full text-center py-8">
              <p className="text-[#2A382A]/70">No news posts available.</p>
            </div>
          ) : (
            news.map((item, index) => (
            <div
              data-news-index={index}
              key={item.id}
              onClick={(e) => {
                e.preventDefault();
                if (item.slug) {
                  window.location.href = `/article/${item.slug}`;
                }
              }}
              className={`group cursor-pointer no-underline transform transition-all duration-700 ${
                visibleNews.has(index)
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-16 opacity-0'
              }`}
            >
              {/* Image Container */}
              <div className="relative aspect-[4/3] mb-4 overflow-hidden rounded-2xl">
                <img 
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <time className="text-[#2A382A]/70 text-sm">
                  {formatDate(new Date(item.published_date), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
                <h3 className="text-[#2A382A] text-xl font-semibold group-hover:text-[#E85C0D] transition-colors">
                  {item.title}
                </h3>
                <p className="text-[#2A382A]/70 text-sm mt-1 line-clamp-2">
                  {item.excerpt}
                </p>
              </div>
            </div>
            ))
          )}
        </div>

        {/* Mobile/Tablet CTA */}
        {/* <div className="mt-8 lg:hidden">
          <Link
            to="#"
            className="w-full flex items-center justify-between px-3 py-2 bg-[#E85C0D] text-white rounded-[1rem] hover:bg-[#ff6a0d] transition-all duration-300 hover:scale-105 text-sm"
          >
            See All News
            <span className="bg-white/20 p-2 rounded-lg ml-6">
              <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        </div> */}
      </div>
    </section>
  );
};

export default NewsUpdates;